import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../../../../services/api';
import '../../css/editor.css';
import { toast } from "react-toastify";
import { confirmToast } from '../../../app/components/confirmToast';

import Save from '../../../../media/save.png';
import Delete from '../../../../media/delete.png';
import Organization from '../../../../media/organization.png';
import Occupied from '../../../../media/person.png';
import Available from '../../../../media/person_empty.png';
import AdminSelect from './admin_select';
import Back from '../../../../media/back.png';

const EditOrganization = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);

  const [countries, setCountries] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [org, setOrg] = useState(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    nation: '',
    city: '',
    create_admin: false,
    admin_first_name: '',
    admin_last_name: '',
    admin_tax_id: '',
    courses: []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgRes, coursesRes, countriesRes] = await Promise.all([
          ApiService.getOrganization(slug),
          ApiService.getCourses(),
          ApiService.getCountries()
        ]);

        setOrg(orgRes);
        setCountries(countriesRes);

        const orgCourses = await ApiService.getOrganizationCourses(orgRes.id);

        const enrichedCourses = coursesRes.map(course => {
          const match = orgCourses.find(c => c.course === course.id);

          return {
            ...course,
            code: match?.code || null,
            max_users: match?.max_users ?? 1,
            occupied_seats: match?.occupied_seats ?? 0,
            available_seats: match?.available_seats ?? 0,
            expires_at: match?.expires_at || '',   
            selected: !!match
          };
        });

        setCoursesList(enrichedCourses);

        setForm({
          name: orgRes.name || '',
          email: orgRes.email || '',
          nation: orgRes.nation || '',
          city: orgRes.city || '',
          create_admin: false,
          admin_first_name: '',
          admin_last_name: '',
          admin_tax_id: '',
          courses: orgCourses.map(c => ({
            id: c.course,
            max_users: c.max_users ?? 1,
            expires_at: c.expires_at || ''   
          }))
        });

      } catch (err) {
        toast.error("Errore caricamento dati");
        navigate("/admin/enti");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, navigate]);

  const handleChange = (e) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const toggleCourse = (courseId) => {
    setForm(prev => {
      const exists = prev.courses.find(c => Number(c.id) === Number(courseId));

      if (exists) {
        return {
          ...prev,
          courses: prev.courses.filter(c => Number(c.id) !== Number(courseId))
        };
      }

      return {
        ...prev,
        courses: [
          ...prev.courses,
          {
            id: courseId,
            max_users: 1,
            expires_at: ''   
          }
        ]
      };
    });
  };

  const updateMaxUsers = (courseId, value) => {
    setForm(prev => ({
      ...prev,
      courses: prev.courses.map(c =>
        Number(c.id) === Number(courseId)
          ? { ...c, max_users: value }
          : c
      )
    }));
  };

  const updateExpiresAt = (courseId, value) => {
    setForm(prev => ({
      ...prev,
      courses: prev.courses.map(c =>
        Number(c.id) === Number(courseId)
          ? { ...c, expires_at: value }
          : c
      )
    }));
  };

  const validate = () => {
    const errs = {};

    if (!form.name.trim()) errs.name = "Campo obbligatorio";
    if (!form.nation) errs.nation = "Campo obbligatorio";
    if (!form.city.trim()) errs.city = "Campo obbligatorio";

    if (form.create_admin) {
        if (!form.admin_first_name)
            errs.admin_first_name = "Campo obbligatorio";

        if (!form.admin_last_name)
            errs.admin_last_name = "Campo obbligatorio";

        if (!form.email)
            errs.email = ['Campo obbligatorio', true];

        if (!form.admin_tax_id)
            errs.admin_tax_id = ['Campo obbligatorio', true];
    }

    return errs;
};

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    try {
      setSaving(true);

      await ApiService.updateOrganization({
        name: form.name,
        email: form.email,
        nation: form.nation,
        city: form.city,
        create_admin: form.create_admin,
        admin_first_name: form.admin_first_name,
        admin_last_name: form.admin_last_name,
        admin_tax_id: form.admin_tax_id,
      }, slug);

      const existing = await ApiService.getOrganizationCourses(org.id);

      const selectedIds = form.courses.map(c => Number(c.id));
      const existingIds = existing.map(r => Number(r.course));

      const toDelete = existing.filter(
        r => !selectedIds.includes(Number(r.course))
      );

      await Promise.all(
        toDelete.map(r =>
          ApiService.deleteOrganizationCourse(r.id)
        )
      );

      const toCreate = form.courses.filter(
        c => !existingIds.includes(Number(c.id))
      );

      await Promise.all(
        toCreate.map(item =>
          ApiService.createOrganizationCourse({
            organization: org.id,
            course: item.id,
            max_users: Number(item.max_users || 1),
            expires_at: item.expires_at || null   
          })
        )
      );

      const toUpdate = form.courses.filter(
        c => existingIds.includes(Number(c.id))
      );

      await Promise.all(
        toUpdate.map(item => {
          const rel = existing.find(
            r => Number(r.course) === Number(item.id)
          );

          if (!rel) return null;

          return ApiService.updateOrganizationCourse(rel.id, {
            max_users: Number(item.max_users || 1),
            expires_at: item.expires_at || null   
          });
        })
      );

      toast.success("Ente aggiornato con successo!");
      navigate("/admin/enti");

    } catch (err) {
      console.error(err);

      if (err.response && err.response.data) {
          setErrors(err.response.data);
      } else {
          toast.error("Errore durante aggiornamento");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    confirmToast({
      message: "Sei sicuro di voler eliminare questo ente?",
      type: "error",
      confirmText: "Elimina",
      onConfirm: async () => {
        try {
          setDeleting(true);
          await ApiService.deleteOrganization(slug);
          toast.success("Ente eliminato con successo");
          navigate("/admin/enti");
        } catch (err) {
          toast.error("Errore eliminazione");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const countryOptions = countries.map(c => ({
    value: c.value,
    label: c.label
  }));

  const getError = (field) => {
      if (!errors[field]) return null;

      return Array.isArray(errors[field])
        ? errors[field][0]
        : errors[field];
    };

  return (
    <form className="editor-form" onSubmit={handleSubmit}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>
          <img src={Organization} className="section-image" />
          Modifica Ente
        </h2>

        <button
          type="button"
          onClick={handleDelete}
          disabled={saving || deleting}
          style={{ backgroundColor: 'crimson' }}
        >
          <img src={Delete} className="button-image2" />
          {deleting ? "Eliminazione..." : "Elimina"}
        </button>
      </div>

      <button
        className="tab-button"
        style={{marginTop:'20px'}}
        onClick={() => navigate(`/admin/enti`)}
        type='button'
      >
        <img src={Back} alt="Modifica" className="button-image" style={{marginTop:'-2.5px', marginRight:'2.5px'}}/>
        Torna all'elenco degli Enti
      </button>

     
      <label>Nome *</label>
      <input type="text" name="name" value={form.name} onChange={handleChange} />

      <label>Nazione *</label>
        <AdminSelect
          options={countryOptions}
          value={countryOptions.find(c => c.value === form.nation) || null}
          onChange={(selected) =>
            setForm(prev => ({
              ...prev,
              nation: selected ? selected.value : ''
            }))
          }
          placeholder="Seleziona una nazione"
        />
        {errors.nation && <span className="error">{errors.nation}</span>}

      <label>Città *</label>
      <input type="text" name="city" value={form.city} onChange={handleChange} />

      {!org?.admin_user && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20 }}>
            <input
              type="checkbox"
              checked={form.create_admin}
              onChange={(e) => {
                const checked = e.target.checked;

                setForm(prev => ({
                  ...prev,
                  create_admin: checked,
                  ...(checked
                    ? {}
                    : {
                        admin_first_name: '',
                        admin_last_name: '',
                        admin_tax_id: '',
                        email: '',
                      }),
                }));
              }}
            />
            Crea un amministratore per questo ente
          </label>


          {form.create_admin && (
            <>
              <label>Nome referente *</label>
              <input
                type="text"
                name="admin_first_name"
                value={form.admin_first_name}
                onChange={handleChange}
              />
              {errors.admin_first_name && <span className="error">{errors.admin_first_name}</span>}

              <label>Cognome referente *</label>
              <input
                type="text"
                name="admin_last_name"
                value={form.admin_last_name}
                onChange={handleChange}
              />
              {errors.admin_last_name && <span className="error">{errors.admin_last_name}</span>}

              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className={`input ${errors.email ? "error" : ""}`}
              />
              {getError('email') && (
                <span className="error">
                  {getError('email')}
                </span>
              )}

              <label>Codice fiscale *</label>
              <input
                type="text"
                name="admin_tax_id"
                maxLength={16}
                value={form.admin_tax_id}
                onChange={handleChange}
                className={`input ${errors.admin_tax_id ? "error" : ""}`}
              />
              {getError('admin_tax_id') && (
                <span className="error">
                  {getError('admin_tax_id')}
                </span>
              )}
            </>
          )}
        </>
      )}

      
      <label style={{ marginTop: 20, display: 'block' }}>
        Corsi abilitati
      </label>

      <div style={{ marginTop: 10 }}>

        {coursesList.map(course => {
          const selected = form.courses.find(
            c => Number(c.id) === Number(course.id)
          );

          return (
            <div
              key={course.id}
              style={{
                padding: 12,
                marginBottom: 10,
                border: '1px solid #e6e6e6',
                borderRadius: 10,
                backgroundColor: selected ? '#fafafa' : '#fff'
              }}
            >

              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={!!selected}
                    onChange={() => toggleCourse(course.id)}
                  />

                  <div style={{ marginLeft: 10 }}>
                    <div style={{ fontWeight: 600 }}>
                      {course.title}
                    </div>

                    {course.code && (
                      <div style={{ fontSize: 12, color: '#666' }}>
                        Codice: {course.code}
                      </div>
                    )}
                  </div>
                </div>

                
                {selected && (
                  <div style={{ display: 'flex', gap: 15, fontSize: 12, color: '#444' }}>
                    <span>
                      <img src={Occupied} style={{width:'24px', height: '24px', marginTop: '-5px'}} />
                      Occupati: <b>{course.occupied_seats}</b>
                    </span>

                    <span>
                      <img src={Available} style={{width:'24px', height: '24px', marginTop: '-5px'}} />
                      Disponibili: <b>{course.available_seats}</b>
                    </span>
                  </div>
                )}

              </div>

             
              {selected && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent:'flex-end' }}>

                
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14, color: '#777' }}>
                      Data scadenza
                    </span>

                    <input
                      type="date"
                      value={selected.expires_at || ''}
                      onChange={(e) => updateExpiresAt(course.id, e.target.value)}
                      style={{
                        padding: 6,
                        borderRadius: 6,
                        border: '1px solid #ddd'
                      }}
                    />
                  </div>

                 
                  <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                    <span style={{ fontSize: 14, color: '#777' }}>
                      Max utenti
                    </span>

                    <input
                      type="number"
                      min={course.occupied_seats || 1}
                      value={selected.max_users}
                      onChange={(e) => updateMaxUsers(course.id, e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      style={{
                        width: 90,
                        padding: 6,
                        borderRadius: 6,
                        border: '1px solid #ddd',
                        textAlign: 'center'
                      }}
                    />
                  </div>

                </div>
              )}

            </div>
          );
        })}

      </div>

      <button type="submit" disabled={saving} style={{ marginTop: 50 }}>
        <img src={Save} className="button-image2" />
        {saving ? "Salvataggio..." : "Salva"}
      </button>

    </form>
  );
};

export default EditOrganization;